import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./StudentPortal.css";
import {
  FaThLarge,
  FaChalkboardTeacher,
  FaHeart,
  FaUserEdit,
  FaSignOutAlt,
  FaBars,
  FaCalendarAlt,
  FaClipboardList,
  FaSearch,
  FaArrowRight,
} from "react-icons/fa";
import { useAuth } from "../../../hooks/AuthProvider";
import MyClasses from "../classes/MyClasses";
import FavouriteTutors from "../favourites/FavouriteTutors";
import StudentAccount from "../account/StudentAccount";
import {
  getUpcomingClasses,
  getBookings,
  getFavorites,
} from "../../../data/modules/student-module";
import OffersStrip from "../../../components/offers/OffersStrip";

const TABS = [
  { key: "overview", label: "Dashboard", icon: <FaThLarge /> },
  { key: "classes", label: "My Classes", icon: <FaChalkboardTeacher /> },
  { key: "favourites", label: "Favourites", icon: <FaHeart /> },
  { key: "profile", label: "Update Profile", icon: <FaUserEdit /> },
];

export default function StudentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activeLabel = TABS.find((t) => t.key === tab)?.label || "Dashboard";

  const setTab = (key) => {
    setSearchParams(key === "overview" ? {} : { tab: key });
    setSidebarOpen(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="sp-shell">
      {/* Mobile overlay */}
      <div
        className={`sp-overlay ${sidebarOpen ? "show" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sp-sidebar ${sidebarOpen ? "open" : ""}`}>
        <nav className="sp-nav">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "active" : ""}
              onClick={() => setTab(t.key)}
            >
              <span className="sp-ico">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </nav>
        <button className="sp-logout" onClick={() => logout()}>
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      {/* Main */}
      <main className="sp-main">
        <div className="sp-mobilebar">
          <button
            className="sp-hamburger"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <FaBars />
          </button>
          <span className="t">{activeLabel}</span>
        </div>

        {tab === "overview" && <Overview user={user} onTab={setTab} navigate={navigate} />}

        {tab === "classes" && (
          <div className="sp-embed">
            <h2 className="sp-pane-title">My Classes</h2>
            <MyClasses />
          </div>
        )}

        {tab === "favourites" && (
          <div className="sp-embed">
            <h2 className="sp-pane-title">Favourite Tutors</h2>
            <FavouriteTutors />
          </div>
        )}

        {tab === "profile" && (
          <div className="sp-embed">
            <h2 className="sp-pane-title">Update Profile</h2>
            <StudentAccount />
          </div>
        )}
      </main>
    </div>
  );
}

/* ----------------------- Overview tab ----------------------- */
function Overview({ user, onTab, navigate }) {
  const [stats, setStats] = useState({ upcoming: 0, total: 0, favourites: 0 });
  const [upcoming, setUpcoming] = useState([]);

  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const placeholder = import.meta.env.VITE_PROFILE_PHOTO_PLACEHOLDER;
  const firstName = user?.fullName ? user.fullName.split(" ")[0] : "there";

  useEffect(() => {
    let alive = true;
    (async () => {
      const [up, all, favs] = await Promise.allSettled([
        getUpcomingClasses(),
        getBookings({ status: "all" }),
        getFavorites(),
      ]);
      if (!alive) return;

      const upClasses =
        up.status === "fulfilled" ? up.value?.classes || [] : [];
      const allBookings =
        all.status === "fulfilled" ? all.value?.bookings || [] : [];
      const favTutors =
        favs.status === "fulfilled" ? favs.value?.tutors || [] : [];

      setUpcoming(upClasses);
      setStats({
        upcoming: upClasses.length,
        total: allBookings.length,
        favourites: favTutors.length,
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  const fmt = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return { day: "--", mon: "" };
    return {
      day: d.getDate(),
      mon: d.toLocaleString("en-US", { month: "short" }),
    };
  };

  return (
    <>
      <div className="sp-greeting">
        <img
          className="sp-avatar"
          src={`${baseUrl}/${user?.profilePhoto ?? ""}`}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = placeholder;
          }}
          alt=""
        />
        <div>
          <h1>Welcome back, {firstName} 👋</h1>
          <p>Here's what's happening with your learning.</p>
        </div>
      </div>

      <OffersStrip copyMode title="Available Offers" />

      <div className="sp-stats">
        <div className="sp-stat-card">
          <div className="sp-stat-icon">
            <FaCalendarAlt />
          </div>
          <div>
            <h3>{stats.upcoming}</h3>
            <span>Upcoming classes</span>
          </div>
        </div>
        <div className="sp-stat-card">
          <div className="sp-stat-icon">
            <FaClipboardList />
          </div>
          <div>
            <h3>{stats.total}</h3>
            <span>Total bookings</span>
          </div>
        </div>
        <div className="sp-stat-card">
          <div className="sp-stat-icon">
            <FaHeart />
          </div>
          <div>
            <h3>{stats.favourites}</h3>
            <span>Favourite tutors</span>
          </div>
        </div>
      </div>

      <div className="sp-section">
        <div className="sp-section-head">
          <h2>Upcoming Classes</h2>
          <button className="sp-link" onClick={() => onTab("classes")}>
            View all <FaArrowRight size={12} />
          </button>
        </div>

        {upcoming.length === 0 ? (
          <div className="sp-empty">
            <p>No upcoming classes. Find a tutor and book your first class!</p>
          </div>
        ) : (
          upcoming.slice(0, 4).map((c) => {
            const dt = fmt(c.startDate || c.date);
            const tutorName =
              c.tutorId?.fullName || c.tutorName || c.tutor?.fullName || "Tutor";
            return (
              <div className="sp-class-item" key={c._id || Math.random()}>
                <div className="sp-class-date">
                  <span className="d">{dt.day}</span>
                  <span className="m">{dt.mon}</span>
                </div>
                <div className="sp-class-body">
                  <strong>{c.subject || "Class"}</strong>
                  <span>with {tutorName}</span>
                </div>
                <div className="sp-class-time">
                  {c.startTime || ""}
                  {c.endTime ? ` – ${c.endTime}` : ""}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="sp-section">
        <div className="sp-section-head">
          <h2>Quick Actions</h2>
        </div>
        <div className="sp-quick">
          <button onClick={() => navigate("/categories")}>
            <span className="sp-ico">
              <FaSearch />
            </span>
            Find Tutors
          </button>
          <button onClick={() => onTab("classes")}>
            <span className="sp-ico">
              <FaChalkboardTeacher />
            </span>
            My Classes
          </button>
          <button onClick={() => onTab("favourites")}>
            <span className="sp-ico">
              <FaHeart />
            </span>
            Favourites
          </button>
          <button onClick={() => onTab("profile")}>
            <span className="sp-ico">
              <FaUserEdit />
            </span>
            Update Profile
          </button>
        </div>
      </div>
    </>
  );
}
